import React, { useState, useEffect } from 'react';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { segmentsAPI, Segment } from '../../services/api';
import { Modal, Button } from '../ui';

interface CreateSegmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  segment?: Segment | null;
}

const CreateSegmentModal: React.FC<CreateSegmentModalProps> = ({
  isOpen,
  onClose,
  segment,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'DYNAMIC' as 'DYNAMIC' | 'STATIC',
    color: '#6366f1',
    criteria: {} as any,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (segment) {
      setFormData({
        name: segment.name,
        description: segment.description || '',
        type: segment.type,
        color: segment.color || '#6366f1',
        criteria: segment.criteria || {},
      });
    } else {
      setFormData({
        name: '',
        description: '',
        type: 'DYNAMIC',
        color: '#6366f1',
        criteria: {},
      });
    }
  }, [segment]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (segment) {
        await segmentsAPI.update(segment.id, formData);
      } else {
        await segmentsAPI.create(formData);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save segment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCriteriaChange = (field: string, value: any) => {
    const newCriteria = { ...(formData.criteria || {}) };
    if (value) {
      newCriteria[field] = value;
    } else {
      delete newCriteria[field];
    }
    setFormData({
      ...formData,
      criteria: newCriteria,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center">
          <FunnelIcon className="h-6 w-6 text-white mr-3" />
          <span>{segment ? 'Edit Segment' : 'Create Segment'}</span>
        </div>
      }
      size="lg"
    >
      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Segment Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., High-value customers"
                className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description..."
                rows={3}
                className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Segment Type *
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'DYNAMIC' | 'STATIC' })}
                className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="DYNAMIC">Dynamic (Auto-updates)</option>
                <option value="STATIC">Static (Manual list)</option>
              </select>
              <p className="mt-1 text-xs text-neutral-500">
                {formData.type === 'DYNAMIC'
                  ? 'Automatically includes clients matching criteria'
                  : 'Manually add/remove clients'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="h-10 w-20 rounded-lg border-neutral-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder="#6366f1"
                  className="flex-1 rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Criteria (for Dynamic segments) */}
          {formData.type === 'DYNAMIC' && (
            <div className="pt-6 border-t border-neutral-200">
              <h4 className="text-sm font-medium text-neutral-900 mb-4">Segment Criteria</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Lifecycle Stage */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Lifecycle Stage
                  </label>
                  <select
                    value={(formData.criteria as any)?.lifecycleStage || ''}
                    onChange={(e) => handleCriteriaChange('lifecycleStage', e.target.value)}
                    className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">All Stages</option>
                    <option value="LEAD">Lead</option>
                    <option value="MQL">Marketing Qualified Lead</option>
                    <option value="SQL">Sales Qualified Lead</option>
                    <option value="OPPORTUNITY">Opportunity</option>
                    <option value="CUSTOMER">Customer</option>
                    <option value="EVANGELIST">Evangelist</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                {/* Lead Status */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Lead Status
                  </label>
                  <select
                    value={(formData.criteria as any)?.leadStatus || ''}
                    onChange={(e) => handleCriteriaChange('leadStatus', e.target.value)}
                    className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="NEW">New</option>
                    <option value="CONTACTED">Contacted</option>
                    <option value="QUALIFIED">Qualified</option>
                    <option value="UNQUALIFIED">Unqualified</option>
                    <option value="NURTURING">Nurturing</option>
                    <option value="CONVERTED">Converted</option>
                    <option value="LOST">Lost</option>
                  </select>
                </div>

                {/* Importance */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Importance
                  </label>
                  <select
                    value={(formData.criteria as any)?.importance || ''}
                    onChange={(e) => handleCriteriaChange('importance', e.target.value)}
                    className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">All Levels</option>
                    <option value="VERY_LOW">Very Low</option>
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="VERY_HIGH">Very High</option>
                  </select>
                </div>

                {/* Lead Score Min */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Min Lead Score
                  </label>
                  <input
                    type="number"
                    value={(formData.criteria as any)?.leadScoreMin || ''}
                    onChange={(e) => handleCriteriaChange('leadScoreMin', e.target.value ? parseInt(e.target.value) : undefined)}
                    placeholder="0"
                    className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={loading}
          >
            {segment ? 'Update Segment' : 'Create Segment'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateSegmentModal;

